import { MediaImage } from '@/lib/mediaDisplay';
import { useState } from 'react';
import { MediaFolder } from '@wordpuppi/shared';
import { ImageInsertDialog } from './ImageInsertDialog';
import { ImageCropDialog } from './ImageCropDialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ImagePlus } from 'lucide-react';

/** Feature image + its alt text and caption (post metadata). */
export interface FeatureImageFields {
  featured_image: string;
  featured_image_alt: string;
  featured_image_caption: string;
}

export const emptyFeatureImage: FeatureImageFields = {
  featured_image: '',
  featured_image_alt: '',
  featured_image_caption: '',
};

interface FeatureImageCardProps {
  siteSlug: string;
  value: FeatureImageFields;
  onChange: (patch: Partial<FeatureImageFields>) => void;
  /** Folder to browse/upload feature images from. Defaults to 'posts'. */
  folder?: MediaFolder;
}

/**
 * Feature image card in post settings: a dashed
 * "Add feature image" affordance when empty, and a wide preview with inline
 * alt text / caption inputs once set. Reuses ImageInsertDialog (browse /
 * upload / paste URL) for picking.
 */
export function FeatureImageCard({ siteSlug, value, onChange, folder = 'posts' }: FeatureImageCardProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  // AB#612: a picked image goes through the crop/reframe step before it's saved.
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState('');

  return (
    <div className="space-y-2">
      {value.featured_image ? (
        <div className="space-y-3">
          {/* AB#551: persisted url is root-relative; desktop's webview origin serves
              no media, so resolve against the API origin for DISPLAY only (as
              MediaBrowser does). AB#556: object-contain + capped height shows the
              true aspect ratio instead of a cropped, viewport-dominating banner. */}
          <MediaImage
                scopeKey={siteSlug}
            src={value.featured_image}
            alt={value.featured_image_alt || 'Feature image'}
            className="w-full max-h-64 rounded-lg border border-border object-contain bg-muted"
          />

          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
              Replace
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(emptyFeatureImage)}
            >
              Remove
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feature-image-alt">Alt text</Label>
            <Input
              id="feature-image-alt"
              value={value.featured_image_alt}
              onChange={(e) => onChange({ featured_image_alt: e.target.value })}
              placeholder="Describe the image for screen readers and search engines."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="feature-image-caption">Caption</Label>
            <Input
              id="feature-image-caption"
              value={value.featured_image_caption}
              onChange={(e) => onChange({ featured_image_caption: e.target.value })}
              placeholder="Optional caption shown under the image."
            />
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-3 py-3 text-sm text-muted-foreground hover:border-foreground/30 hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
        >
          <ImagePlus className="h-4 w-4" />
          Add feature image
        </button>
      )}

      <ImageInsertDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        siteSlug={siteSlug}
        folder={folder}
        // ponytail: URL only — the dialog's upload tab passes the filename as `alt`,
        // which is worse than no alt text. The user types alt below.
        // AB#612: don't save straight away — reframe/crop it first.
        onInsert={(url) => {
          setCropSrc(url);
          setCropOpen(true);
        }}
      />

      <ImageCropDialog
        open={cropOpen}
        onOpenChange={setCropOpen}
        siteSlug={siteSlug}
        folder={folder}
        src={cropSrc}
        onCropped={(url) => onChange({ featured_image: url })}
      />
    </div>
  );
}
